import { useMemo, useCallback, useEffect, useState } from "react";
import {
  App as AntApp,
  Typography,
  Button,
  Card,
  Spin,
  Empty,
  Select,
  Form,
  Divider,
  Space,
  Result,
  Modal,
  Segmented,
  Tag,
} from "antd";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useTherapistSesionController } from "./useTherapistSesionController";
import { useSessionEvaluationPicker } from "./useSessionEvaluationPicker";
import { SessionVrInfoPanel } from "./SessionVrInfoPanel";
import { buildAnswerItems } from "../../../../../components/revi/EvaluationAnswersForm/buildAnswerItems";
import { MotricityCodeLinker } from "../../../../../components/Admin/TherapistCmp/MotricityCmp";
import { LiveStreamViewer } from "../../../../../components/Admin/TherapistCmp/LiveStreamCmp";
import { confirmar } from "../../../../../ui/confirmar";
import { useMovil } from "../../../../../hooks/useIsNarrow";
import { esSalaUnityEmbebida } from "../../../../../Utils/salasUnity";
import "./Page_Therapist_Sesion.scss";

const { Title, Text } = Typography;

function hasEmbedToken(url) {
  if (!url || typeof url !== "string") return false;
  return url.includes("embed_token=") || url.includes("embedToken=");
}

// Solo exigimos embed_token para URLs de Mozilla Hubs (donde el token
// es lo que permite el embebido). Para escenas WebGL/A-Frame/Unity en
// otros dominios (p.ej. *.educa360experiences.com, buzzvr.*) NO aplica:
// su embebibilidad depende de su cabecera X-Frame-Options o CSP, no
// de ningun token de Mozilla.
//
// NOTA: usamos endsWith con dominios exactos en vez de includes(). El
// matcher anterior `host.includes("educa360")` matcheaba ademas
// educa360experiences.com y educa360.com (ambos VR externos), por lo
// que les pedia un embed_token que jamas tendrian.
function shouldRequireEmbedToken(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    const host = (u.hostname || "").toLowerCase();
    // Mozilla Hubs (instancia propia educa360.es/educa360.org + hubs.mozilla.com)
    return (
      host === "educa360.es" ||
      host.endsWith(".educa360.es") ||
      host === "educa360.org" ||
      host.endsWith(".educa360.org") ||
      host === "hubs.mozilla.com" ||
      host.endsWith(".hubs.mozilla.com")
    );
  } catch {
    return false;
  }
}

/**
 * SOLO para decidir si mostramos el modal de tipo de sala:
 * - educa360.org + /scenes/<id> o ?sceneId=...
 * - o un id "pelado"
 */
function isEducaHubSceneSource(sceneUrl) {
  const raw = String(sceneUrl || "").trim();
  if (!raw) return false;

  const looksLikeBareId =
    !raw.includes("://") && !raw.includes("/") && raw.length >= 6;
  if (looksLikeBareId) return true;

  try {
    const u = new URL(raw);
    const host = (u.hostname || "").toLowerCase();
    if (!host.endsWith("educa360.es") && !host.endsWith("educa360.org"))
      return false;

    if ((u.pathname || "").includes("/scenes/")) return true;
    if (u.searchParams.get("sceneId") || u.searchParams.get("scene_id"))
      return true;

    return false;
  } catch {
    return false;
  }
}

function isEducaHubDirectSceneUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    const host = (u.hostname || "").toLowerCase();
    return (
      (host.endsWith("educa360.es") || host.endsWith("educa360.org")) &&
      (u.pathname || "").includes("/scenes/")
    );
  } catch {
    return false;
  }
}

// M12.2 - una URL es un "video embebible conocido" si pertenece a un
// proveedor que normalizeEmbedUrl del server reconoce (YouTube embed,
// Vimeo player) o es un fichero multimedia. Cualquier otra URL externa
// que no sea Hubs se asume juego VR y NO debe pintarse en iframe (era
// una segunda instancia inutil del juego para el terapeuta).
function isKnownVideoEmbed(url) {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  if (lower.includes("youtube.com/embed/")) return true;
  if (lower.includes("player.vimeo.com/")) return true;
  return /\.(mp4|webm|ogg|m4v|mov|jpg|jpeg|png|gif)(\?|#|$)/.test(lower);
}

/* -----------------------------
   Subcomponentes
-------------------------------- */
function SessionRoomCard({
  currentStep,
  session,
  launchVR,
  nextScene,
  closeSession,
  openRoomInNewTab,
  messageApi,
}) {
  // Solo rotulos. El titulo de la terapia, el de la escena y el codigo de
  // sala son datos: entran sin traducir.
  const { t } = useTranslation(["therapy", "common"]);
  const movil = useMovil();

  const confirmarFinalizar = useCallback(
    () =>
      confirmar({
        titulo: t("sesion.sala.finalizarConfirmaTitulo"),
        consecuencia: t("sesion.sala.finalizarConfirmaTexto"),
        textoOk: t("sesion.sala.finalizar"),
        onOk: closeSession,
      }),
    [closeSession, t],
  );

  const hubsUrl = session?.hubsRoomUrl || "";
  const embedded = hasEmbedToken(hubsUrl);
  const requireToken = shouldRequireEmbedToken(hubsUrl);
  const isDirectEducaScene = isEducaHubDirectSceneUrl(hubsUrl) && !embedded;

  const totalScenes = session?.therapy?.scenes?.length || 0;
  const currentIndex = Number(session?.currentSceneIndex || 0);
  const hasMultipleScenes = totalScenes > 1;
  const isLastScene = currentIndex >= totalScenes - 1;

  const sceneUrl = currentStep?.virtualScene?.module_01?.ModOneUrlsala || "";
  const isEducaHubScene = isEducaHubSceneSource(sceneUrl);

  const [modeOpen, setModeOpen] = useState(false);
  const [launching, setLaunching] = useState(false);

  const doLaunch = useCallback(
    async (roomMode) => {
      // Abrimos pestaña “ya” para que el navegador no lo bloquee.
      // Solo para Sala única en escenas educaHub (URL directa).
      const shouldAutoOpenNewTab = isEducaHubScene && roomMode === "individual";
      const popup = shouldAutoOpenNewTab
        ? window.open("about:blank", "_blank", "noopener,noreferrer")
        : null;

      if (shouldAutoOpenNewTab && !popup) {
        messageApi?.warning?.(t("sesion.sala.avisoPopup"));
      }

      try {
        setLaunching(true);

        if (isEducaHubScene) {
          await launchVR({ roomMode });
        } else {
          await launchVR();
        }

        // Si es Sala única, redirigimos la pestaña a la URL directa de escena
        if (shouldAutoOpenNewTab && popup) {
          popup.location.href = sceneUrl;
        }
      } finally {
        setLaunching(false);
        setModeOpen(false);
      }
    },
    [launchVR, isEducaHubScene, sceneUrl, messageApi, t],
  );

  const onGenerateClick = useCallback(() => {
    // Modal SOLO para escenas educaHub
    if (isEducaHubScene) setModeOpen(true);
    else doLaunch(); // no-hubs / webgl / room-url => directo
  }, [isEducaHubScene, doLaunch]);

  // La portada de la sala, si la tiene. Llega poblada desde el endpoint de
  // sesion (populate anidado de `module_01.bannertop`); una sala sin portada
  // devuelve el campo vacio y la tarjeta se pinta exactamente como antes.
  const portadaUrl = currentStep?.virtualScene?.module_01?.bannertop?.url || "";
  const tituloSala =
    currentStep?.virtualScene?.module_01?.ModOneTitle ||
    t("sesion.contexto.salaSinTitulo");

  return (
    <Card title={t("sesion.sala.titulo")} variant="outlined">
      {/* Se distingue la sala de un vistazo, sin leer. La imagen manda sobre
          el texto a proposito: al pasar de sala, lo que cambia primero en el
          ojo es la portada.

          - Proporcion FIJA (16:9 via aspect-ratio en el scss) y object-fit
            cover: cualquier imagen que suba el creador de salas encaja sin
            deformarse y sin mover el resto de la tarjeta. La altura de la
            tarjeta pasa a ser estable, que es lo que pide "no descuadrar".
          - `key` con la URL: al avanzar de sala el navegador no deja la
            portada anterior congelada mientras carga la nueva.
          - alt vacio y no el titulo: el titulo esta justo debajo como texto
            real; repetirlo en el alt hace que un lector de pantalla lo lea
            dos veces seguidas.
          - Si la imagen falla al cargar (fichero borrado del bucket), se
            oculta sola: un icono de imagen rota informa menos que nada. */}
      {portadaUrl ? (
        <img
          key={portadaUrl}
          src={portadaUrl}
          alt=""
          loading="lazy"
          className="revi-sala-portada"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}

      <Text strong>{tituloSala}</Text>

      <Divider />

      <div className="actions">
        <Button type="primary" onClick={onGenerateClick} loading={launching}>
          {t("sesion.sala.generar")}
        </Button>

        {hasMultipleScenes ? (
          <Button onClick={nextScene} disabled={isLastScene}>
            {t("sesion.sala.siguiente")}
          </Button>
        ) : null}

        {/*
          Cerraba la sesion y navegaba fuera sin preguntar nada, con el paciente
          dentro de las gafas. Sigue donde estaba; lo que cambia es que ahora
          nombra la consecuencia antes de hacerla.
        */}
        <Button danger onClick={confirmarFinalizar}>
          {t("sesion.sala.finalizar")}
        </Button>
      </div>

      <Modal
        open={modeOpen}
        title={t("sesion.modo.titulo")}
        onCancel={() => setModeOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModeOpen(false)}>
            {t("common:actions.cancel")}
          </Button>,
          <Button
            key="individual"
            onClick={() => doLaunch("individual")}
            loading={launching}
          >
            {t("sesion.modo.unica")}
          </Button>,
          <Button
            key="shared"
            type="primary"
            onClick={() => doLaunch("shared")}
            loading={launching}
          >
            {t("sesion.modo.compartida")}
          </Button>,
        ]}
      >
        <Text>{t("sesion.modo.intro")}</Text>
        <ul style={{ marginTop: 8 }}>
          <li>
            <Trans
              i18nKey="sesion.modo.compartidaDetalle"
              t={t}
              components={{ b: <b /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="sesion.modo.unicaDetalle"
              t={t}
              components={{ b: <b /> }}
            />
          </li>
        </ul>
      </Modal>

      {session?.hubsRoomUrl ? (
        <div className="vr-meta">
          {/*
            El codigo de sala es lo unico de esta pantalla que el terapeuta
            tiene que LEER EN VOZ ALTA, con el paciente poniendose las gafas
            delante. En movil sube a placa y a tamano de titular, en vez de ir
            en letra pequena detras de una URL de doscientos caracteres.
          */}
          {movil && session.hubsRoomCode ? (
            <div className="vr-meta__placa">
              <span className="vr-meta__placaRotulo">
                {t("sesion.sala.codigoRotulo")}
              </span>
              <span className="vr-meta__placaCodigo">
                {session.hubsRoomCode}
              </span>
            </div>
          ) : null}

          {/* La URL lleva el embed_token: son cientos de caracteres que nadie
              teclea ni dicta. En movil se accede por el boton. */}
          {!movil ? (
            <>
              <Text type="secondary">{t("sesion.sala.urlEtiqueta")}</Text>
              <div className="vr-meta__url">{session.hubsRoomUrl}</div>
            </>
          ) : null}

          <Space style={{ marginTop: 10 }}>
            <Button onClick={openRoomInNewTab}>
              {t("sesion.sala.abrirPestana")}
            </Button>
            {!movil && session.hubsRoomCode ? (
              <Text type="secondary">
                <Trans
                  i18nKey="sesion.sala.codigoSala"
                  t={t}
                  values={{ codigo: session.hubsRoomCode }}
                  components={{ b: <b /> }}
                />
              </Text>
            ) : null}
          </Space>

          {isDirectEducaScene ? (
            <div style={{ marginTop: 10 }}>
              <Text type="secondary">
                <Trans
                  i18nKey="sesion.sala.avisoUnica"
                  t={t}
                  components={{ b: <b /> }}
                />
              </Text>
            </div>
          ) : requireToken && !embedded ? (
            <div style={{ marginTop: 10 }}>
              <Text type="secondary">
                <Trans
                  i18nKey="sesion.sala.avisoToken"
                  t={t}
                  components={{ b: <b /> }}
                />
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

// Recibe `t` porque es una funcion de modulo: si tradujera por dentro, se
// evaluaria una vez y se quedaria clavada en el idioma del arranque.
function buildEvalOption(ev, t) {
  const origin =
    ev?.type === "standardized"
      ? { text: t("sesion.evaluacion.origenEstandar"), color: "blue" }
      : ev?.visibility === "global"
        ? { text: t("sesion.evaluacion.origenGlobal"), color: "purple" }
        : ev?.visibility === "public"
          ? { text: t("sesion.evaluacion.origenPublica"), color: "cyan" }
          : null;

  return {
    label: (
      <Space size={6}>
        <span>{ev.title}</span>
        {origin ? (
          <Tag color={origin.color} style={{ marginInlineEnd: 0 }}>
            {origin.text}
          </Tag>
        ) : null}
      </Space>
    ),
    value: ev._id,
    title: ev.title,
  };
}

function SessionEvaluationCard({
  sceneEvaluations,
  patients,
  submitEvaluationForPatients,
  accessToken,
  therapistId,
  messageApi,
}) {
  // Solo rotulos. El titulo de la terapia, el de la escena y el codigo de
  // sala son datos: entran sin traducir.
  const { t } = useTranslation(["therapy", "common"]);
  const movil = useMovil();

  const [form] = Form.useForm();
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);

  const picker = useSessionEvaluationPicker({
    accessToken,
    therapistId,
    sceneEvaluations,
    notify: messageApi,
  });

  const {
    source,
    setSource,
    currentList,
    loadingList,
    indicatedCount,
    selectedEvaluationId,
    pickEvaluation,
    pickedTemplate,
    loadingPickedDetail,
  } = picker;

  useEffect(() => {
    form.resetFields();
  }, [selectedEvaluationId, form]);

  useEffect(() => {
    setSelectedPatientIds([]);
  }, [sceneEvaluations]);

  const segmentOptions = useMemo(
    () => [
      {
        label: indicatedCount
          ? t("sesion.evaluacion.indicadasConCuenta", { count: indicatedCount })
          : t("sesion.evaluacion.indicadas"),
        value: "indicated",
      },
      { label: t("sesion.evaluacion.generales"), value: "general" },
    ],
    [indicatedCount, t],
  );

  const selectOptions = useMemo(
    () => (currentList || []).map((ev) => buildEvalOption(ev, t)),
    [currentList, t],
  );

  const placeholderBySource = {
    indicated: t("sesion.evaluacion.placeholderIndicadas"),
    general: t("sesion.evaluacion.placeholderGenerales"),
  };

  const emptyBySource = {
    indicated: t("sesion.evaluacion.vacioIndicadas"),
    general: t("sesion.evaluacion.vacioGenerales"),
  };

  const onSubmit = useCallback(async () => {
    if (!selectedEvaluationId)
      return messageApi.warning(t("sesion.evaluacion.avisoSelecciona"));
    if (selectedPatientIds.length === 0)
      return messageApi.warning(t("sesion.evaluacion.avisoSinPaciente"));
    if (!pickedTemplate)
      return messageApi.warning(t("sesion.evaluacion.avisoCargando"));

    // Sin este catch, un cuestionario incompleto rechazaba la promesa y el
    // boton no hacia NADA visible: con un Ryff de 42 items, un callejon sin
    // salida. Ahora se dice cuantas respuestas faltan y el Form salta a la
    // primera.
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      // `scrollToFirstError` del Form solo actua cuando antd valida por su
      // cuenta al enviar; aqui se valida a mano, asi que el salto hay que
      // pedirlo. Sin esto el aviso decia cuantas faltan pero no llevaba a
      // ninguna, que con 42 items es lo mismo que no decir nada.
      // Solo en movil: en escritorio el cuestionario cabe de un vistazo y
      // saltar la pantalla sola es un cambio de comportamiento que nadie ha
      // pedido.
      const primero = err?.errorFields?.[0]?.name;
      if (movil && primero) form.scrollToField(primero, { block: "center" });
      return messageApi.warning(
        t("sesion.evaluacion.avisoIncompleto", {
          count: err?.errorFields?.length || 0,
        }),
      );
    }
    const qObj = values?.q || {};

    const answers = Object.entries(qObj).map(([questionId, value]) => {
      if (pickedTemplate?.subtype === "checklist")
        return { questionId, value: value ? 1 : 0 };
      return { questionId, value };
    });

    await submitEvaluationForPatients({
      evaluationId: selectedEvaluationId,
      patientIds: selectedPatientIds,
      answers,
    });

    form.resetFields();
  }, [
    movil,
    selectedEvaluationId,
    selectedPatientIds,
    form,
    pickedTemplate,
    submitEvaluationForPatients,
    messageApi,
    t,
  ]);

  const showSpinner = loadingList || loadingPickedDetail;
  const listIsEmpty = !loadingList && (currentList?.length || 0) === 0;

  return (
    <Card
      title={t("sesion.evaluacion.titulo")}
      variant="outlined"
      style={{ marginTop: 14 }}
    >
      <div className="eval-controls">
        <Segmented
          block
          value={source}
          onChange={setSource}
          options={segmentOptions}
        />

        <Select
          showSearch
          placeholder={placeholderBySource[source]}
          value={selectedEvaluationId}
          onChange={(v) => pickEvaluation(v)}
          style={{ width: "100%" }}
          loading={loadingList}
          disabled={loadingList}
          options={selectOptions}
          notFoundContent={
            loadingList ? <Spin size="small" /> : emptyBySource[source]
          }
          filterOption={(input, option) =>
            (option?.title || "")
              .toLowerCase()
              .includes((input || "").toLowerCase())
          }
        />

        <Select
          mode="multiple"
          placeholder={t("sesion.evaluacion.placeholderUsuarios")}
          value={selectedPatientIds}
          onChange={setSelectedPatientIds}
          style={{ width: "100%" }}
          options={(patients || []).map((p) => ({
            label: `${p.name} ${p.surname}`,
            value: p._id,
          }))}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError={{ behavior: "smooth", block: "center" }}
      >
        {showSpinner ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        ) : listIsEmpty ? (
          <Empty description={emptyBySource[source]} />
        ) : !pickedTemplate ? (
          <Empty description={t("sesion.evaluacion.sinSeleccion")} />
        ) : (
          <>
            {buildAnswerItems(pickedTemplate, t)}
            <Button type="primary" onClick={onSubmit}>
              {t("sesion.evaluacion.guardar")}
            </Button>
          </>
        )}
      </Form>
    </Card>
  );
}

function SessionVrCard({
  session,
  currentStep,
  launchVR,
  openRoomInNewTab,
  accessToken,
  activeMotricityLink,
  sceneDetection,
}) {
  // Solo rotulos. El titulo de la terapia, el de la escena y el codigo de
  // sala son datos: entran sin traducir.
  const { t } = useTranslation(["therapy", "common"]);
  const movil = useMovil();

  const url = session?.hubsRoomUrl || "";
  const embedded = hasEmbedToken(url);
  const requireToken = shouldRequireEmbedToken(url);

  const isDirectEducaScene = isEducaHubDirectSceneUrl(url) && !embedded;

  // M12 live stream: si la escena es A-Frame/WebGL externa (no Hubs) y
  // el linker tiene un code pending cuyo gameKey soporta live stream,
  // sustituimos el iframe por el viewer del stream del paciente. El
  // iframe original era una segunda instancia del juego (no la sesion
  // del paciente), por lo que para A-Frame no tenia valor clinico.
  // Las escenas de Mozilla Hubs siguen embebiendose en iframe sin cambios.
  const sceneSourceUrl =
    currentStep?.virtualScene?.module_01?.ModOneUrlsala || "";
  const isHubsScene = isEducaHubSceneSource(sceneSourceUrl);

  // Una de las cinco salas Unity de educa360experiences. Se mira tanto la URL
  // del catalogo como la de la sala ya lanzada: antes de pulsar Generar solo
  // existe la primera, y el lanzamiento directo copia una en otra sin tocarla.
  const esUnity =
    esSalaUnityEmbebida(sceneSourceUrl) || esSalaUnityEmbebida(url);

  const showLiveViewer =
    !isHubsScene &&
    !esUnity &&
    !!activeMotricityLink &&
    activeMotricityLink.supportsLiveStream === true &&
    !!activeMotricityLink.code &&
    !!accessToken;

  // M12.2 - decidimos si la escena es "juego VR" (no embebible) o no:
  //   - Si es Hubs -> NO juego VR (cae al iframe Hubs legacy).
  //   - Si la auto-deteccion del catalogo encontro match -> SI juego VR.
  //   - Como red de seguridad: si la URL externa no es video conocido
  //     (YouTube/Vimeo/mp4...), asumimos juego VR. Asi el iframe queda
  //     reservado para videos y Hubs, nunca para juegos cuyos iframes
  //     son solo una segunda instancia inutil para el terapeuta.
  //   - Si es una de las cinco salas Unity -> NO, se embebe (ver salasUnity.js).
  //     Va por delante de `sceneDetection` a proposito: la autodeteccion del
  //     catalogo llega a dar un falso positivo con estas salas -en Orquesta
  //     Sinfonica proponia "Alfareria"-, y sin este freno el terapeuta se
  //     quedaba esperando el codigo de un juego que no es el suyo.
  const isIdentifiedVrGame =
    !isHubsScene &&
    !esUnity &&
    ((sceneDetection && sceneDetection.detected === true) ||
      (!!url && !isKnownVideoEmbed(url)));

  return (
    <Card title={t("sesion.contexto.salaSinTitulo")} variant="outlined">
      <SessionVrInfoPanel
        therapy={session?.therapy}
        virtualScene={currentStep?.virtualScene}
      />

      <div style={{ height: 12 }} />

      {showLiveViewer ? (
        <LiveStreamViewer
          accessToken={accessToken}
          code={activeMotricityLink.code}
          gameKey={activeMotricityLink.gameKey}
          gameLabel={activeMotricityLink.gameLabel}
        />
      ) : isIdentifiedVrGame ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ textAlign: "center" }}>
              <Text strong>{t("sesion.vr.esperandoCodigo")}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("sesion.vr.esperandoCodigoAyuda")}
              </Text>
            </div>
          }
        />
      ) : !url ? (
        <Empty description={t("sesion.vr.sinGenerar")} />
      ) : esUnity && movil ? (
        /*
          En escritorio esta sala se ve aqui dentro. En un movil no: es una
          escena Unity a pantalla casi completa, que es justo lo que ya se
          retiro del resto de la pagina porque el dedo se quedaba atrapado en
          la escena 3D al intentar bajar. Ademas la sala es del PACIENTE; lo
          que el terapeuta necesita del telefono es abrirla, no jugarla.
        */
        <Result
          status="info"
          title={t("sesion.vr.unityMovilTitulo")}
          subTitle={t("sesion.vr.unityMovilDetalle")}
          extra={
            <Button type="primary" onClick={openRoomInNewTab}>
              {t("sesion.sala.abrirPestana")}
            </Button>
          }
        />
      ) : requireToken && !embedded ? (
        <Result
          status={isDirectEducaScene ? "info" : "warning"}
          title={
            isDirectEducaScene
              ? t("sesion.vr.tituloUnica")
              : t("sesion.vr.tituloNoEmbebible")
          }
          subTitle={
            isDirectEducaScene
              ? t("sesion.vr.detalleUnica")
              : t("sesion.vr.detalleNoEmbebible")
          }
          extra={[
            <Button key="newtab" onClick={openRoomInNewTab}>
              {t("sesion.sala.abrirPestana")}
            </Button>,
            <Button
              key="shared"
              type="primary"
              onClick={() => launchVR({ roomMode: "shared" })}
            >
              {t("sesion.vr.crearCompartida")}
            </Button>,
          ]}
        />
      ) : (
        <>
          <iframe
            title={t("sesion.vr.iframeTitulo")}
            src={url} // Asegurar que es formato embed: https://www.youtube.com/embed/ID?rel=0
            className="vr-iframe"
            // LA CLAVE MAESTRA: Lista completa de permisos para sensores y VR
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; xr-spatial-tracking"
            allowFullScreen
          />

          {/*
            Salida por si el embebido falla en un navegador concreto.

            Un iframe que no carga no avisa: se queda en blanco, y desde fuera
            no hay forma de preguntarle que le pasa porque es otro origen. Sin
            esta linea, el terapeuta tendria delante un cuadro blanco sin nada
            que leer, con el paciente esperando.

            Solo para las salas Unity. Las de Hubs y los videos llevan anos
            embebiendose y no necesitan que se les dude en pantalla.
          */}
          {esUnity ? (
            <Text
              type="secondary"
              style={{ display: "block", marginTop: 8, fontSize: 12 }}
            >
              {t("sesion.vr.unityAyuda")}
            </Text>
          ) : null}
        </>
      )}
    </Card>
  );
}

/* -----------------------------
   Vista
-------------------------------- */
export function PageTherapistSesionInner() {
  const navigate = useNavigate();
  const { message: messageApi } = AntApp.useApp();
  // Solo rotulos. El titulo de la terapia, el de la escena y el codigo de
  // sala son datos: entran sin traducir.
  const { t } = useTranslation(["therapy", "common"]);

  const {
    loading,
    session,
    currentStep,
    accessToken,
    therapistId,
    launchVR,
    nextScene,
    closeSession,
    submitEvaluationForPatients,
    openRoomInNewTab,
  } = useTherapistSesionController({ messageApi });

  // M12 live stream: estado del code activo (el ultimo pending). Lo emite
  // MotricityCodeLinker y lo consume SessionVrCard para decidir si abre
  // el LiveStreamViewer en lugar del iframe legacy. Solo aplica para
  // escenas A-Frame/WebGL externas (NO Hubs).
  const [activeMotricityLink, setActiveMotricityLink] = useState(null);
  // M12.2 - resultado de la auto-deteccion del juego a partir de la
  // escena actual. Lo emite MotricityCodeLinker y lo consume
  // SessionVrCard para sustituir el iframe legacy por placeholder cuando
  // la escena es claramente VR.
  const [sceneDetection, setSceneDetection] = useState(null);

  return (
    <div className="revi-page-sesion">
      <div className="revi-page-sesion__main">
        <div className="revi-page-sesion__header">
          <Button
            type="link"
            onClick={() => navigate("/portal/therapist/terapia")}
          >
            ← {t("common:actions.back")}
          </Button>

          <Title level={3} style={{ margin: 0 }}>
            {session?.therapy?.title || t("sesion.sesionSinTitulo")}
          </Title>

          <Text type="secondary">
            {t("sesion.salaDeTotal", {
              actual: Number(session?.currentSceneIndex || 0) + 1,
              total: session?.therapy?.scenes?.length || "-",
            })}
          </Text>
        </div>

        {loading ? (
          <div className="loader">
            <Spin size="large" />
          </div>
        ) : !session || !currentStep ? (
          <Empty description={t("sesion.errorCarga")} />
        ) : (
          <div className="revi-page-sesion__grid">
            <div className="revi-page-sesion__left">
              <SessionRoomCard
                currentStep={currentStep}
                session={session}
                launchVR={launchVR}
                nextScene={nextScene}
                closeSession={closeSession}
                openRoomInNewTab={openRoomInNewTab}
                messageApi={messageApi}
              />

              {/* M12 - Si la escena NO es Mozilla Hubs, mostramos el
                  panel de vinculacion del codigo del juego VR externo.
                  El sceneUrl viene de ModOneUrlsala, y reutilizamos el
                  detector isEducaHubSceneSource definido arriba en este
                  mismo modulo. */}
              {(() => {
                const sceneUrl =
                  currentStep?.virtualScene?.module_01?.ModOneUrlsala || "";
                if (!sceneUrl) return null;
                if (isEducaHubSceneSource(sceneUrl)) return null;
                // Las cinco salas Unity no emiten codigo. El panel pedia uno
                // que nunca iba a llegar y encima proponia el juego
                // equivocado, asi que aqui no pinta nada.
                if (esSalaUnityEmbebida(sceneUrl)) return null;
                const sceneTitle =
                  currentStep?.virtualScene?.module_01?.ModOneTitle || "";
                return (
                  <MotricityCodeLinker
                    accessToken={accessToken}
                    activeSessionId={session?._id || session?.sessionId}
                    patients={session?.patients || []}
                    sceneIndex={Number(session?.currentSceneIndex || 0)}
                    onActiveLinkChange={setActiveMotricityLink}
                    sceneUrl={sceneUrl}
                    sceneTitle={sceneTitle}
                    onSceneDetection={setSceneDetection}
                  />
                );
              })()}

              <SessionEvaluationCard
                sceneEvaluations={currentStep?.evaluations || []}
                patients={session?.patients || []}
                submitEvaluationForPatients={submitEvaluationForPatients}
                accessToken={accessToken}
                therapistId={therapistId}
                messageApi={messageApi}
              />
            </div>

            <div className="revi-page-sesion__right">
              <SessionVrCard
                session={session}
                currentStep={currentStep}
                launchVR={launchVR}
                openRoomInNewTab={openRoomInNewTab}
                accessToken={accessToken}
                activeMotricityLink={activeMotricityLink}
                sceneDetection={sceneDetection}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
